import React, { useEffect, useState } from 'react'
import { portal } from '../portalApi.js'
import { fmt } from '../api.js'
import NoteDrawer from '../components/NoteDrawer.jsx'

// 홈 — 오늘 처리할 일 + 이번달 미확정 현황 + 팀 할일
const KIND_LABEL = { bank: '계좌', tax: '세금계산서', card: '신용카드' }
const STATUS_COLS = [['todo', '할 일'], ['doing', '진행 중'], ['done', '완료']]
const 억 = (v) => (v == null ? '…' : (v / 1e8).toFixed(1))

export default function Home({ month, onMonthChange, currentUserId, autoOpenNoteId, onAutoOpened }) {
  const [summary, setSummary] = useState(null)
  const [ov, setOv] = useState(null)
  const [unconfirmed, setUnconfirmed] = useState(null)
  const [tasks, setTasks] = useState(null)
  const [users, setUsers] = useState([])
  const [form, setForm] = useState({ title: '', assigneeId: '', dueDate: '' })
  const [err, setErr] = useState('')
  const [toast, setToast] = useState('')

  const notify = (m) => { setToast(m); setTimeout(() => setToast(''), 2500) }

  const loadSummary = () => portal.get('/api/dashboard/summary').then(setSummary).catch((e) => setErr(e.message))
  // 흩어져 있던 것을 한 화면에 모으는 게 이 시스템의 요점인데, 그동안 메뉴를 하나씩
  // 눌러가며 설명해야 했다. 있는 데이터만 담는다 - 없는 칸은 왜 없는지를 적는다.
  const loadOverview = (m) => portal.get(`/api/dashboard/overview?month=${m}`).then(setOv).catch(() => setOv(null))
  const loadTasks = () => portal.get('/api/dashboard/tasks').then(setTasks).catch(() => setTasks([]))

  useEffect(() => { setOv(null); loadOverview(month) }, [month])

  useEffect(() => {
    loadSummary()
    loadTasks()
    portal.get('/api/auth/users-brief').then(setUsers).catch(() => {})
    // 캐시 DB에서 즉시 읽음 (데이터 화면에서 수집 동기화 시 갱신)
    portal.get('/api/dashboard/unconfirmed').then(setUnconfirmed).catch(() => setUnconfirmed({}))
  }, [])

  const dailyCheck = async () => {
    try { await portal.post('/api/dashboard/daily-check'); notify('자금일보 확인 완료'); loadSummary() }
    catch (e) { setErr(e.message) }
  }

  const addTask = async () => {
    if (!form.title.trim()) return
    try {
      await portal.post('/api/dashboard/tasks', {
        title: form.title.trim(),
        assigneeId: form.assigneeId ? Number(form.assigneeId) : null,
        dueDate: form.dueDate || null,
      })
      setForm({ title: '', assigneeId: '', dueDate: '' })
      loadTasks()
    } catch (e) { setErr(e.message) }
  }

  const setStatus = async (id, status) => {
    try { await portal.post(`/api/dashboard/tasks/${id}`, { status }); loadTasks() }
    catch (e) { setErr(e.message) }
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <h2>홈</h2>
        <NoteDrawer target="home" screenKey={null}
          context={{ month }} contextLabel={month}
          onNavigate={(ctx) => ctx?.month && onMonthChange?.(ctx.month)}
          autoOpenNoteId={autoOpenNoteId} onAutoOpened={onAutoOpened}
          currentUserId={currentUserId} />
      </div>
      {err && <div className="error-box">{err}</div>}

      {/* 요약 — 자금·수지·수집·원가를 한 줄로 */}
      <div className="ov-row">
        <div className="ov ov-lead">
          <div className="ov-k">보유 자금 <span className="muted">{ov?.['기준일'] || month} 기준</span></div>
          <div className="ov-v">{ov ? 억(ov['자금']['합계']) : '…'}<small>억</small></div>
          <div className="ov-d">
            {ov ? `은행 ${억(ov['자금']['은행'])}억 · 증권 ${억(ov['자금']['증권'])}억 · 계좌 ${ov['자금']['계좌수']}개` : ''}
          </div>
        </div>
        <div className="ov">
          <div className="ov-k">{month} 수입</div>
          <div className="ov-v">{ov ? 억(ov['수지']['수입']) : '…'}<small>억</small></div>
          <div className="ov-d">{ov ? `${fmt(ov['수지']['건수'])}건 (계좌 간 이동 제외)` : ''}</div>
        </div>
        <div className="ov">
          <div className="ov-k">{month} 지출</div>
          <div className="ov-v bad">{ov ? 억(ov['수지']['지출'] ? -ov['수지']['지출'] : 0) : '…'}<small>억</small></div>
          <div className="ov-d">{ov ? `대체 ${억(ov['수지']['대체'])}억은 뺐습니다` : ''}</div>
        </div>
        <div className="ov">
          <div className="ov-k">{month} 순증감</div>
          <div className={'ov-v' + (ov && ov['수지']['순증감'] < 0 ? ' bad' : '')}>
            {ov ? 억(ov['수지']['순증감']) : '…'}<small>억</small>
          </div>
          <div className="ov-d">
            {ov ? `수입 − 지출 · 잔액 ${억(ov['자금']['합계'])}억` : '수입 − 지출'}
          </div>
        </div>
      </div>

      <div className="ov-row ov-sub">
        <div className="ov">
          <div className="ov-k">수집한 거래</div>
          <div className="ov-v sm">{ov ? fmt(Object.values(ov['수집']).reduce((s, v) => s + v['건수'], 0)) : '…'}<small>건</small></div>
          <div className="ov-d">
            {ov ? ['bank', 'card', 'tax', 'securities'].map((k) => {
              const L = { bank: '은행', card: '카드', tax: '세금계산서', securities: '증권' }[k]
              return `${L} ${fmt(ov['수집'][k]['건수'])}`
            }).join(' · ') : ''}
          </div>
        </div>
        <div className="ov">
          <div className="ov-k">분류 진행</div>
          <div className="ov-v sm">
            {ov && ov['분류']['현금건수']
              ? `${Math.round(ov['분류']['거래처붙음'] / ov['분류']['현금건수'] * 100)}%` : '…'}
          </div>
          <div className="ov-d">
            {ov ? `거래처 ${fmt(ov['분류']['거래처붙음'])} / ${fmt(ov['분류']['현금건수'])}건 · 계정과목 ${fmt(ov['분류']['계정과목붙음'])}건` : ''}
          </div>
        </div>
        <div className="ov">
          <div className="ov-k">제조원가 <span className="muted">{ov?.['원가']?.['최근월'] || ''}</span></div>
          <div className="ov-v sm">{ov?.['원가']?.['제조원가'] ? 억(ov['원가']['제조원가']) : '—'}<small>억</small></div>
          <div className="ov-d">
            {ov?.['원가']?.['최근월']
              ? `품목 ${fmt(ov['원가']['품목수'])} · 생산 ${fmt(ov['원가']['생산수량'])}`
              : '자료 없음'}
          </div>
        </div>
        <div className="ov">
          <div className="ov-k">
            매출액 <span className="muted">{ov?.['손익']?.['연도'] ? `${ov['손익']['연도']}년 누적` : ''}</span>
          </div>
          <div className="ov-v sm">{ov?.['손익']?.['매출액'] ? 억(ov['손익']['매출액']) : '—'}<small>억</small></div>
          <div className="ov-d">
            {ov?.['손익']?.['매출액']
              ? `영업이익 ${억(ov['손익']['영업이익'])}억 · 당기순이익 ${억(ov['손익']['당기순이익'])}억`
              : '결산 > 손익계산서에서 위하고 파일을 올리면 채워집니다'}
          </div>
        </div>
      </div>

      <div className="cards">
        <div className="card">
          <div className="label">승인 대기 전표</div>
          <div className="value">{summary ? fmt(summary.pendingVouchers ?? 0) : '…'}건</div>
        </div>
        <div className="card">
          <div className="label">전자결재 미매칭 이체</div>
          <div className="value">{summary ? fmt(summary.unmatchedApproval ?? 0) : '…'}건</div>
        </div>
        <div className="card">
          <div className="label">오늘 자금일보 확인</div>
          {summary?.dailyCheck
            ? <div className="value ok" style={{ fontSize: 16 }}>✓ {summary.dailyCheck.checked_by_name}</div>
            : <button className="btn" style={{ marginTop: 4 }} onClick={dailyCheck}>확인 완료 표시</button>}
        </div>
      </div>

      <div className="panel">
        <h3>이번달 미확정 현황 <span className="muted" style={{ fontSize: 12 }}>· 우리 DB 기준 (각 데이터 화면에서 동기화하면 갱신)</span></h3>
        <table style={{ maxWidth: 720 }}>
          <thead>
            <tr><th className="l">데이터</th><th>이번달 건수</th><th>계정과목 미확정</th><th>적요 미확정</th><th>거래처 미매칭</th></tr>
          </thead>
          <tbody>
            {unconfirmed && Object.keys(unconfirmed).length === 0 && (
              <tr><td colSpan={5} className="c muted">표시할 항목이 없습니다</td></tr>
            )}
            {unconfirmed && Object.entries(unconfirmed).map(([kind, v]) => (
              <tr key={kind}>
                <td className="l">{KIND_LABEL[kind] ?? kind}</td>
                {v.error
                  ? <td colSpan={4} className="l warn">조회 실패: {v.error}</td>
                  : <>
                      <td>{fmt(v.count)}</td>
                      <td className={v.unconfirmedCategory ? 'warn' : ''}>{fmt(v.unconfirmedCategory)}</td>
                      <td className={v.unconfirmedDesc ? 'warn' : ''}>{v.unconfirmedDesc === undefined ? '-' : fmt(v.unconfirmedDesc)}</td>
                      <td className={v.unmatchedVendor ? 'warn' : ''}>{fmt(v.unmatchedVendor)}</td>
                    </>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <h3>팀 할일</h3>
        <div className="row" style={{ marginBottom: 12 }}>
          <input placeholder="할일 제목" style={{ width: 260 }} value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && addTask()} />
          <select value={form.assigneeId} onChange={(e) => setForm({ ...form, assigneeId: e.target.value })}>
            <option value="">담당자 없음</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <input type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
          <button className="btn" onClick={addTask} disabled={!form.title.trim()}>추가</button>
        </div>
        <div className="kanban">
          {STATUS_COLS.map(([st, label]) => (
            <div className="kanban-col" key={st}>
              <div className="kanban-title">{label} · {tasks ? tasks.filter((t) => t.status === st).length : 0}</div>
              {tasks?.filter((t) => t.status === st).map((t) => (
                <div className="kanban-card" key={t.id}>
                  <div>{t.title}</div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {t.assignee_name || '담당 미지정'}{t.due_date ? ` · ~${t.due_date}` : ''}
                  </div>
                  <div className="row" style={{ gap: 4, marginTop: 6 }}>
                    {STATUS_COLS.filter(([s]) => s !== st).map(([s, l]) => (
                      <button key={s} className="btn secondary" style={{ padding: '2px 8px', fontSize: 11 }}
                        onClick={() => setStatus(t.id, s)}>{l}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      {toast && <div className="toast">{toast}</div>}
    </>
  )
}
